import React, { useState, useEffect, useCallback } from 'react';
import {
    DollarSign, Search, Calendar, User, Clock, AlertTriangle,
    CheckCircle, History, Eye, Users, FileText, RefreshCw, Wallet
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import apiService from '../../services/apiService';
import SucursalFilter from '../../components/filters/SucursalFilter';
import { formatMoney, formatDate } from '../../utils/formatters';
import './CuentasPorCobrarPage.css';

// Obtener primer y ultimo dia del mes actual
const getDefaultDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
        inicio: firstDay.toISOString().split('T')[0],
        fin: lastDay.toISOString().split('T')[0]
    };
};

const CuentasPorCobrarPage = () => {
    const { showToast, user } = useApp();
    const { canRegisterPaymentCXC, canViewHistorialCXC } = usePermissions();

    // Estado local de loading para evitar ciclos
    const [loading, setLoading] = useState(false);

    // Estados principales
    const [cuentas, setCuentas] = useState([]);
    const [filteredCuentas, setFilteredCuentas] = useState([]);
    const [resumenPorCliente, setResumenPorCliente] = useState([]);
    const [resumen, setResumen] = useState({
        total_cuentas: 0,
        total_pendiente: 0,
        total_vencido: 0,
        cuentas_vencidas: 0,
        cuentas_proximas: 0
    });

    // Filtros
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCliente, setFilterCliente] = useState('todos');
    const [filterUrgencia, setFilterUrgencia] = useState('todos');
    const [fechaInicio, setFechaInicio] = useState(getDefaultDates().inicio);
    const [fechaFin, setFechaFin] = useState(getDefaultDates().fin);
    const [sucursalId, setSucursalId] = useState('');

    // Tab activo
    const [activeTab, setActiveTab] = useState('listado');

    // Modales
    const [showPagoModal, setShowPagoModal] = useState(false);
    const [showHistorialModal, setShowHistorialModal] = useState(false);
    const [showDetalleModal, setShowDetalleModal] = useState(false);
    const [showAbonoGlobalModal, setShowAbonoGlobalModal] = useState(false);
    const [showCuentasClienteModal, setShowCuentasClienteModal] = useState(false);

    // Datos de modales
    const [cuentaActual, setCuentaActual] = useState(null);
    const [historialPagos, setHistorialPagos] = useState(null);
    const [detalleCuenta, setDetalleCuenta] = useState(null);
    const [clienteAbonoGlobal, setClienteAbonoGlobal] = useState(null);
    const [cuentasClienteGlobal, setCuentasClienteGlobal] = useState([]);
    const [resultadoAbonoGlobal, setResultadoAbonoGlobal] = useState(null);
    const [clienteCuentasModal, setClienteCuentasModal] = useState(null);
    const [cuentasDelCliente, setCuentasDelCliente] = useState([]);

    // Formulario de pago
    const [pagoForm, setPagoForm] = useState({
        monto: '',
        metodo_pago: 'efectivo',
        referencia: '',
        notas: ''
    });

    // Formulario de abono global
    const [abonoGlobalForm, setAbonoGlobalForm] = useState({
        monto: '',
        metodo_pago: 'efectivo',
        referencia: '',
        notas: ''
    });

    // Cargar datos
    const loadCuentas = useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiService.getCuentasPorCobrar({
                fecha_inicio: fechaInicio,
                fecha_fin: fechaFin,
                cliente_id: filterCliente !== 'todos' ? filterCliente : null,
                urgencia: filterUrgencia !== 'todos' ? filterUrgencia : null,
                sucursal: sucursalId
            });

            if (result.success) {
                setCuentas(result.data.cuentas || []);
                setResumen(result.data.resumen || {});
            } else {
                showToast(result.message || 'Error al cargar cuentas', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('Error al cargar cuentas por cobrar', 'error');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fechaInicio, fechaFin, filterCliente, filterUrgencia, sucursalId]);

    const loadResumenPorCliente = useCallback(async () => {
        try {
            const result = await apiService.getResumenCXCPorCliente();
            if (result.success) {
                setResumenPorCliente(result.data.clientes || []);
            }
        } catch (error) {
            console.error('Error cargando resumen:', error);
        }
    }, []);

    useEffect(() => {
        loadCuentas();
    }, [loadCuentas]);

    useEffect(() => {
        if (activeTab === 'resumen') {
            loadResumenPorCliente();
        }
    }, [activeTab, loadResumenPorCliente]);

    // Filtrar cuentas localmente por busqueda
    useEffect(() => {
        let filtered = cuentas;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(cuenta =>
                cuenta.folio?.toLowerCase().includes(query) ||
                cuenta.cliente_nombre?.toLowerCase().includes(query) ||
                cuenta.cliente_nombre_comercial?.toLowerCase().includes(query)
            );
        }

        setFilteredCuentas(filtered);
    }, [cuentas, searchQuery]);

    // Handlers
    const handleOpenPagoModal = (cuenta) => {
        setCuentaActual(cuenta);
        setPagoForm({
            monto: '',
            metodo_pago: 'efectivo',
            referencia: '',
            notas: ''
        });
        setShowPagoModal(true);
    };

    const handlePagoFormChange = (field, value) => {
        setPagoForm(prev => ({ ...prev, [field]: value }));
    };

    const handleRegistrarPago = async () => {
        if (!cuentaActual) return;

        const monto = parseFloat(pagoForm.monto);
        if (isNaN(monto) || monto <= 0) {
            showToast('Ingrese un monto valido', 'error');
            return;
        }

        if (monto > cuentaActual.saldo_pendiente) {
            showToast('El monto excede el saldo pendiente', 'error');
            return;
        }

        try {
            setLoading(true);
            const result = await apiService.registrarPagoCXC({
                venta_id: cuentaActual.id,
                monto: monto,
                metodo_pago: pagoForm.metodo_pago,
                referencia: pagoForm.referencia || null,
                notas: pagoForm.notas || null,
                usuario_id: user?.user_id || user?.id
            });

            if (result.success) {
                showToast(result.message, 'success');
                setShowPagoModal(false);
                setCuentaActual(null);
                loadCuentas();
                if (activeTab === 'resumen') {
                    loadResumenPorCliente();
                }
            } else {
                showToast(result.message || 'Error al registrar pago', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('Error al registrar el pago', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleVerHistorial = async (cuenta) => {
        try {
            setLoading(true);
            const result = await apiService.getHistorialPagosVenta(cuenta.id);

            if (result.success) {
                setHistorialPagos(result.data);
                setShowHistorialModal(true);
            } else {
                showToast('Error al cargar historial', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('Error al cargar historial de pagos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleVerDetalle = async (cuenta) => {
        try {
            setLoading(true);
            const result = await apiService.getDetalleCuentaCXC(cuenta.id);

            if (result.success) {
                setDetalleCuenta(result.data);
                setShowDetalleModal(true);
            } else {
                showToast('Error al cargar detalle', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('Error al cargar detalle de cuenta', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterByCliente = (clienteId) => {
        setFilterCliente(clienteId.toString());
        setActiveTab('listado');
    };

    // Handler para abrir modal de cuentas del cliente
    const handleOpenCuentasClienteModal = async (cliente) => {
        try {
            setLoading(true);
            const result = await apiService.getCuentasPendientesCliente(cliente.cliente_id);

            if (result.success) {
                setClienteCuentasModal({
                    id: cliente.cliente_id,
                    nombre: cliente.cliente_nombre,
                    nombre_comercial: cliente.nombre_comercial,
                    total_pendiente: cliente.total_pendiente,
                    num_cuentas: cliente.num_cuentas,
                    cuentas_vencidas: cliente.cuentas_vencidas
                });
                setCuentasDelCliente(result.data.cuentas || []);
                setShowCuentasClienteModal(true);
            } else {
                showToast(result.message || 'Error al cargar cuentas', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('Error al cargar cuentas del cliente', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleResetFilters = () => {
        const defaults = getDefaultDates();
        setFechaInicio(defaults.inicio);
        setFechaFin(defaults.fin);
        setFilterCliente('todos');
        setFilterUrgencia('todos');
        setSearchQuery('');
    };

    // Handlers para Abono Global
    const handleOpenAbonoGlobalModal = async (clienteId) => {
        try {
            setLoading(true);
            const result = await apiService.getCuentasPendientesCliente(clienteId);

            if (result.success) {
                setClienteAbonoGlobal(result.data.cliente);
                setCuentasClienteGlobal(result.data.cuentas || []);
                setAbonoGlobalForm({
                    monto: '',
                    metodo_pago: 'efectivo',
                    referencia: '',
                    notas: ''
                });
                setResultadoAbonoGlobal(null);
                setShowAbonoGlobalModal(true);
            } else {
                showToast(result.message || 'Error al cargar cuentas del cliente', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('Error al cargar cuentas del cliente', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAbonoGlobalFormChange = (field, value) => {
        setAbonoGlobalForm(prev => ({ ...prev, [field]: value }));
    };

    const handleRegistrarAbonoGlobal = async () => {
        if (!clienteAbonoGlobal) return;

        const monto = parseFloat(abonoGlobalForm.monto);
        if (!monto || monto <= 0) {
            showToast('Ingrese un monto valido', 'error');
            return;
        }

        const userId = user?.id || user?.usuario_id || user?.user_id;
        if (!userId) {
            showToast('Error: Usuario no identificado', 'error');
            return;
        }

        try {
            setLoading(true);
            const result = await apiService.registrarAbonoGlobalCXC({
                cliente_id: clienteAbonoGlobal.id,
                monto: monto,
                metodo_pago: abonoGlobalForm.metodo_pago,
                referencia: abonoGlobalForm.referencia,
                notas: abonoGlobalForm.notas,
                registrado_por: userId
            });

            if (result.success) {
                setResultadoAbonoGlobal(result.data);
                showToast(result.message, 'success');
                loadCuentas();
                loadResumenPorCliente();
            } else {
                showToast(result.message || 'Error al registrar abono', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('Error al registrar abono global', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseAbonoGlobalModal = () => {
        setShowAbonoGlobalModal(false);
        setClienteAbonoGlobal(null);
        setCuentasClienteGlobal([]);
        setResultadoAbonoGlobal(null);
    };

    // Calcular distribucion previa del abono
    const calcularDistribucionPrevia = (monto) => {
        if (!monto || monto <= 0 || cuentasClienteGlobal.length === 0) return [];

        let restante = monto;
        const distribucion = [];

        for (const cuenta of cuentasClienteGlobal) {
            if (restante <= 0) break;

            const aplicar = Math.min(restante, cuenta.saldo_pendiente);
            distribucion.push({
                folio: cuenta.folio,
                saldo_actual: cuenta.saldo_pendiente,
                monto_aplicar: aplicar,
                nuevo_saldo: cuenta.saldo_pendiente - aplicar,
                liquidada: aplicar >= cuenta.saldo_pendiente
            });

            restante -= aplicar;
        }

        return distribucion;
    };

    // Obtener clase de urgencia
    const getUrgenciaClass = (urgencia) => {
        switch (urgencia) {
            case 'vencida': return 'urgencia-vencida';
            case 'proxima': return 'urgencia-proxima';
            default: return 'urgencia-normal';
        }
    };

    // Obtener badge de urgencia
    const UrgenciaBadge = ({ urgencia, diasParaVencer }) => {
        const config = {
            vencida: { icon: AlertTriangle, label: 'Vencida', className: 'badge-vencida' },
            proxima: { icon: Clock, label: 'Por vencer', className: 'badge-proxima' },
            normal: { icon: CheckCircle, label: 'Al dia', className: 'badge-normal' }
        };

        const { icon: Icon, label, className } = config[urgencia] || config.normal;

        return (
            <span className={`urgencia-badge ${className}`}>
                <Icon size={14} />
                <span>{label}</span>
                {diasParaVencer !== undefined && (
                    <span className="dias">
                        ({diasParaVencer > 0 ? `${diasParaVencer}d` : `${Math.abs(diasParaVencer)}d atrás`})
                    </span>
                )}
            </span>
        );
    };

    // Lista de clientes unicos para el filtro
    const clientesUnicos = [...new Map(
        cuentas.map(c => [c.cliente_id, { id: c.cliente_id, nombre: c.cliente_nombre }])
    ).values()];

    return (
        <div className="cxc-page">
            {/* Tabs */}
            <div className="cxc-tabs">
                <button
                    className={`cxc-tab ${activeTab === 'listado' ? 'active' : ''}`}
                    onClick={() => setActiveTab('listado')}
                >
                    <FileText size={18} />
                    Cuentas Pendientes
                </button>
                <button
                    className={`cxc-tab ${activeTab === 'resumen' ? 'active' : ''}`}
                    onClick={() => setActiveTab('resumen')}
                >
                    <Users size={18} />
                    Resumen por Cliente
                </button>
            </div>

            {/* Cards de resumen */}
            <div className="cxc-summary-cards">
                <div className="summary-card total">
                    <div className="summary-icon">
                        <DollarSign size={24} />
                    </div>
                    <div className="summary-content">
                        <span className="summary-label">Total por Cobrar</span>
                        <span className="summary-value">{formatMoney(resumen.total_pendiente || 0)}</span>
                    </div>
                </div>

                <div className="summary-card cuentas">
                    <div className="summary-icon">
                        <FileText size={24} />
                    </div>
                    <div className="summary-content">
                        <span className="summary-label">Cuentas Pendientes</span>
                        <span className="summary-value">{resumen.total_cuentas || 0}</span>
                    </div>
                </div>

                <div className="summary-card vencidas">
                    <div className="summary-icon">
                        <AlertTriangle size={24} />
                    </div>
                    <div className="summary-content">
                        <span className="summary-label">Vencidas</span>
                        <span className="summary-value">{resumen.cuentas_vencidas || 0}</span>
                        <span className="summary-amount">{formatMoney(resumen.total_vencido || 0)}</span>
                    </div>
                </div>

                <div className="summary-card proximas">
                    <div className="summary-icon">
                        <Clock size={24} />
                    </div>
                    <div className="summary-content">
                        <span className="summary-label">Por Vencer (7d)</span>
                        <span className="summary-value">{resumen.cuentas_proximas || 0}</span>
                    </div>
                </div>
            </div>

            {/* Controles y filtros - Solo en tab listado */}
            {activeTab === 'listado' && (
                <div className="cxc-controls">
                    <div className="search-filters">

                        <div className="search-container">
                            <div className="date-field">
                                <label>Desde</label>
                                <Input
                                    placeholder="Buscar por folio o cliente..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    icon={<Search size={20} />}
                                    className="search-input"
                                />
                            </div>
                        </div>

                        <div className="date-filters">
                            <div className="date-field">
                                <label>Desde</label>
                                <input
                                    type="date"
                                    value={fechaInicio}
                                    onChange={(e) => setFechaInicio(e.target.value)}
                                    className="date-input"
                                />
                            </div>
                            <div className="date-field">
                                <label>Hasta</label>
                                <input
                                    type="date"
                                    value={fechaFin}
                                    onChange={(e) => setFechaFin(e.target.value)}
                                    className="date-input"
                                />
                            </div>
                        </div>

                        <select
                            value={filterCliente}
                            onChange={(e) => setFilterCliente(e.target.value)}
                            className="filter-select"
                        >
                            <option value="todos">Todos los clientes</option>
                            {clientesUnicos.map(cliente => (
                                <option key={cliente.id} value={cliente.id}>
                                    {cliente.nombre}
                                </option>
                            ))}
                        </select>

                        <select
                            value={filterUrgencia}
                            onChange={(e) => setFilterUrgencia(e.target.value)}
                            className="filter-select urgencia-select"
                        >
                            <option value="todos">Todas</option>
                            <option value="vencida">Vencidas</option>
                            <option value="proxima">Por vencer</option>
                            <option value="normal">Al dia</option>
                        </select>

                        <SucursalFilter value={sucursalId} onChange={setSucursalId} />

                        <button
                            className="reset-filters-btn"
                            onClick={handleResetFilters}
                            title="Restablecer filtros"
                        >
                            <RefreshCw size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Tab: Listado de Cuentas */}
            {activeTab === 'listado' && (
                <div className="cxc-table-container">
                    {loading && (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Cargando cuentas...</p>
                        </div>
                    )}

                    {!loading && filteredCuentas.length === 0 && (
                        <div className="empty-state">
                            <DollarSign size={48} />
                            <h3>No hay cuentas pendientes</h3>
                            <p>No se encontraron cuentas por cobrar con los filtros seleccionados</p>
                        </div>
                    )}

                    {!loading && filteredCuentas.length > 0 && (
                        <table className="cxc-table">
                            <thead>
                                <tr>
                                    <th>Folio</th>
                                    <th>Fecha Venta</th>
                                    <th>Cliente</th>
                                    <th>Total</th>
                                    <th>Abonado</th>
                                    <th>Saldo</th>
                                    <th>Vencimiento</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCuentas.map(cuenta => (
                                    <tr key={cuenta.id} className={getUrgenciaClass(cuenta.urgencia)}>
                                        <td>
                                            <span className="folio">#{cuenta.folio || cuenta.id}</span>
                                        </td>
                                        <td>
                                            <div className="fecha-cell">
                                                <Calendar size={14} />
                                                {formatDate(cuenta.fecha_venta)}
                                            </div>
                                        </td>
                                        <td className="td-cliente">
                                            <div className="cliente-info">
                                                <span className="cliente-nombre">{cuenta.cliente_nombre}</span>
                                                {cuenta.cliente_nombre_comercial && (
                                                    <span className="cliente-comercial">{cuenta.cliente_nombre_comercial}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="monto">{formatMoney(cuenta.total)}</td>
                                        <td className="monto abonado">
                                            {cuenta.total_abonado > 0 ? formatMoney(cuenta.total_abonado) : '-'}
                                            {cuenta.num_abonos > 0 && (
                                                <span className="num-abonos">({cuenta.num_abonos})</span>
                                            )}
                                        </td>
                                        <td className="monto saldo">{formatMoney(cuenta.saldo_pendiente)}</td>
                                        <td>
                                            <div className="vencimiento-cell">
                                                {formatDate(cuenta.fecha_vencimiento)}
                                            </div>
                                        </td>
                                        <td>
                                            <UrgenciaBadge
                                                urgencia={cuenta.urgencia}
                                                diasParaVencer={cuenta.dias_para_vencer}
                                            />
                                        </td>
                                        <td className="acciones">
                                            <div className="acciones-btns">
                                                {canRegisterPaymentCXC() && (
                                                    <button
                                                        className="action-btn pagar"
                                                        onClick={() => handleOpenPagoModal(cuenta)}
                                                        title="Registrar pago"
                                                    >
                                                        <DollarSign size={16} />
                                                    </button>
                                                )}
                                                {canViewHistorialCXC() && cuenta.num_abonos > 0 && (
                                                    <button
                                                        className="action-btn historial"
                                                        onClick={() => handleVerHistorial(cuenta)}
                                                        title="Ver historial de pagos"
                                                    >
                                                        <History size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    className="action-btn detalle"
                                                    onClick={() => handleVerDetalle(cuenta)}
                                                    title="Ver detalle"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan="5" className="total-label">Total Pendiente:</td>
                                    <td className="total-value">{formatMoney(resumen.total_pendiente || 0)}</td>
                                    <td colSpan="3"></td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            )}

            {/* Tab: Resumen por Cliente */}
            {activeTab === 'resumen' && (
                <div className="cxc-table-container">
                    {resumenPorCliente.length === 0 ? (
                        <div className="empty-state">
                            <Users size={48} />
                            <h3>Sin cuentas pendientes</h3>
                            <p>No hay clientes con cuentas por cobrar</p>
                        </div>
                    ) : (
                        <table className="cxc-table resumen-table">
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th>Cuentas</th>
                                    <th>Total Pendiente</th>
                                    <th>Monto Vencido</th>
                                    <th>Vencidas</th>
                                    <th>Credito Disponible</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resumenPorCliente.map(cliente => (
                                    <tr key={cliente.cliente_id} className={cliente.tiene_vencidas ? 'tiene-vencidas' : ''}>
                                        <td className="td-cliente">
                                            <div className="cliente-info">
                                                <span className="cliente-nombre">{cliente.cliente_nombre}</span>
                                                {cliente.nombre_comercial && (
                                                    <span className="cliente-comercial">{cliente.nombre_comercial}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <span className="badge-cuentas">{cliente.num_cuentas}</span>
                                        </td>
                                        <td className="monto total-pendiente">
                                            {formatMoney(cliente.total_pendiente)}
                                        </td>
                                        <td className={`monto ${cliente.monto_vencido > 0 ? 'vencido' : ''}`}>
                                            {cliente.monto_vencido > 0 ? formatMoney(cliente.monto_vencido) : '-'}
                                        </td>
                                        <td className="text-center">
                                            {cliente.cuentas_vencidas > 0 ? (
                                                <span className="badge-vencidas">
                                                    <AlertTriangle size={12} />
                                                    {cliente.cuentas_vencidas}
                                                </span>
                                            ) : (
                                                <span className="badge-ok">
                                                    <CheckCircle size={12} />
                                                </span>
                                            )}
                                        </td>
                                        <td className="monto credito-disponible">
                                            {formatMoney(cliente.credito_disponible)}
                                        </td>
                                        <td className="acciones">
                                            <div className="acciones-btns">
                                                <button
                                                    className="action-btn detalle"
                                                    onClick={() => handleOpenCuentasClienteModal(cliente)}
                                                    title="Ver cuentas"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                {canRegisterPaymentCXC() && (
                                                    <button
                                                        className="action-btn pagar"
                                                        onClick={() => handleOpenAbonoGlobalModal(cliente.cliente_id)}
                                                        title="Abono Global"
                                                    >
                                                        <Wallet size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="total-label">Totales:</td>
                                    <td className="text-center">
                                        <strong>{resumenPorCliente.reduce((sum, c) => sum + c.num_cuentas, 0)}</strong>
                                    </td>
                                    <td className="total-value">
                                        {formatMoney(resumenPorCliente.reduce((sum, c) => sum + c.total_pendiente, 0))}
                                    </td>
                                    <td className="total-value vencido">
                                        {formatMoney(resumenPorCliente.reduce((sum, c) => sum + (c.monto_vencido || 0), 0))}
                                    </td>
                                    <td className="text-center">
                                        <strong>{resumenPorCliente.reduce((sum, c) => sum + c.cuentas_vencidas, 0)}</strong>
                                    </td>
                                    <td colSpan="2"></td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            )}

            {/* Modal: Registrar Pago */}
            {showPagoModal && cuentaActual && (
                <Modal
                    title="Registrar Pago / Abono"
                    onClose={() => {
                        setShowPagoModal(false);
                        setCuentaActual(null);
                    }}
                    size="medium"
                >
                    <div className="pago-modal">
                        {/* Info de la cuenta */}
                        <div className="cuenta-info-card">
                            <div className="info-row">
                                <span className="label">Folio:</span>
                                <span className="value">#{cuentaActual.folio || cuentaActual.id}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Cliente:</span>
                                <span className="value">{cuentaActual.cliente_nombre}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Total Venta:</span>
                                <span className="value">{formatMoney(cuentaActual.total)}</span>
                            </div>
                            <div className="info-row highlight">
                                <span className="label">Saldo Pendiente:</span>
                                <span className="value saldo">{formatMoney(cuentaActual.saldo_pendiente)}</span>
                            </div>
                        </div>

                        {/* Formulario */}
                        <div className="pago-form">
                            <div className="form-field">
                                <label>Monto a Abonar *</label>
                                <div className="monto-input-wrapper">
                                    <span className="currency">$</span>
                                    <input
                                        type="number"
                                        value={pagoForm.monto}
                                        onChange={(e) => handlePagoFormChange('monto', e.target.value)}
                                        placeholder="0.00"
                                        min="0"
                                        max={cuentaActual.saldo_pendiente}
                                        step="0.01"
                                        className="monto-input"
                                    />
                                </div>
                                <div className="quick-amounts">
                                    <button
                                        type="button"
                                        onClick={() => handlePagoFormChange('monto', cuentaActual.saldo_pendiente)}
                                        className="quick-btn"
                                    >
                                        Pagar Total
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handlePagoFormChange('monto', (cuentaActual.saldo_pendiente / 2).toFixed(2))}
                                        className="quick-btn"
                                    >
                                        50%
                                    </button>
                                </div>
                            </div>

                            <div className="form-field">
                                <label>Metodo de Pago *</label>
                                <select
                                    value={pagoForm.metodo_pago}
                                    onChange={(e) => handlePagoFormChange('metodo_pago', e.target.value)}
                                    className="select-input"
                                >
                                    <option value="efectivo">Efectivo</option>
                                    <option value="tarjeta">Tarjeta</option>
                                    <option value="transferencia">Transferencia</option>
                                </select>
                            </div>

                            <div className="form-field">
                                <label>Referencia (opcional)</label>
                                <input
                                    type="text"
                                    value={pagoForm.referencia}
                                    onChange={(e) => handlePagoFormChange('referencia', e.target.value)}
                                    placeholder="No. de transaccion"
                                    className="text-input"
                                />
                            </div>

                            <div className="form-field">
                                <label>Notas (opcional)</label>
                                <textarea
                                    value={pagoForm.notas}
                                    onChange={(e) => handlePagoFormChange('notas', e.target.value)}
                                    placeholder="Notas adicionales..."
                                    rows={2}
                                    className="textarea-input"
                                />
                            </div>
                        </div>

                        {/* Preview del pago */}
                        {pagoForm.monto && parseFloat(pagoForm.monto) > 0 && (
                            <div className="pago-preview">
                                <div className="preview-row">
                                    <span>Saldo actual:</span>
                                    <span>{formatMoney(cuentaActual.saldo_pendiente)}</span>
                                </div>
                                <div className="preview-row abono">
                                    <span>Abono:</span>
                                    <span>- {formatMoney(parseFloat(pagoForm.monto) || 0)}</span>
                                </div>
                                <div className="preview-row nuevo-saldo">
                                    <span>Nuevo saldo:</span>
                                    <span>
                                        {formatMoney(Math.max(0, cuentaActual.saldo_pendiente - (parseFloat(pagoForm.monto) || 0)))}
                                    </span>
                                </div>
                                {parseFloat(pagoForm.monto) >= cuentaActual.saldo_pendiente && (
                                    <div className="liquidada-alert">
                                        <CheckCircle size={16} />
                                        La cuenta quedara liquidada
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Acciones */}
                        <div className="modal-actions">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowPagoModal(false);
                                    setCuentaActual(null);
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleRegistrarPago}
                                disabled={loading || !pagoForm.monto || parseFloat(pagoForm.monto) <= 0}
                            >
                                <CheckCircle size={16} />
                                Registrar Pago
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal: Historial de Pagos */}
            {showHistorialModal && historialPagos && (
                <Modal
                    title={`Historial de Pagos - ${historialPagos.venta?.folio || ''}`}
                    onClose={() => {
                        setShowHistorialModal(false);
                        setHistorialPagos(null);
                    }}
                    size="medium"
                >
                    <div className="historial-modal">
                        <div className="historial-resumen">
                            <div className="resumen-item">
                                <span className="label">Total Venta:</span>
                                <span className="value">{formatMoney(historialPagos.venta?.total)}</span>
                            </div>
                            <div className="resumen-item">
                                <span className="label">Total Abonado:</span>
                                <span className="value verde">{formatMoney(historialPagos.total_abonado)}</span>
                            </div>
                            <div className="resumen-item">
                                <span className="label">Saldo Pendiente:</span>
                                <span className="value rojo">{formatMoney(historialPagos.venta?.saldo_pendiente)}</span>
                            </div>
                        </div>

                        {historialPagos.pagos?.length > 0 ? (
                            <table className="historial-table">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Monto</th>
                                        <th>Metodo</th>
                                        <th>Referencia</th>
                                        <th>Registrado por</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historialPagos.pagos.map((pago, index) => (
                                        <tr key={index}>
                                            <td>{formatDate(pago.fecha_pago)}</td>
                                            <td className="monto">{formatMoney(pago.monto)}</td>
                                            <td>
                                                <span className={`metodo-badge ${pago.metodo_pago}`}>
                                                    {pago.metodo_pago}
                                                </span>
                                            </td>
                                            <td>{pago.referencia || '-'}</td>
                                            <td>{pago.registrado_por || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="no-pagos">No hay pagos registrados</div>
                        )}

                        <div className="modal-actions">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowHistorialModal(false);
                                    setHistorialPagos(null);
                                }}
                            >
                                Cerrar
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal: Detalle de Cuenta */}
            {showDetalleModal && detalleCuenta && (
                <Modal
                    title={`Detalle de Cuenta - ${detalleCuenta.venta?.folio || ''}`}
                    onClose={() => {
                        setShowDetalleModal(false);
                        setDetalleCuenta(null);
                    }}
                    size="large"
                >
                    <div className="detalle-modal">
                        {/* Info general */}
                        <div className="detalle-section">
                            <h4>Informacion General</h4>
                            <div className="detalle-grid">
                                <div className="detalle-item">
                                    <span className="label">Folio:</span>
                                    <span className="value">#{detalleCuenta.venta?.folio}</span>
                                </div>
                                <div className="detalle-item">
                                    <span className="label">Fecha:</span>
                                    <span className="value">{formatDate(detalleCuenta.venta?.fecha_creacion)}</span>
                                </div>
                                <div className="detalle-item">
                                    <span className="label">Cliente:</span>
                                    <span className="value">{detalleCuenta.cliente?.nombre}</span>
                                </div>
                                <div className="detalle-item">
                                    <span className="label">Vendedor:</span>
                                    <span className="value">{detalleCuenta.venta?.vendedor || '-'}</span>
                                </div>
                                <div className="detalle-item">
                                    <span className="label">Vencimiento:</span>
                                    <span className="value">{formatDate(detalleCuenta.venta?.fecha_vencimiento)}</span>
                                </div>
                                <div className="detalle-item">
                                    <span className="label">Estado:</span>
                                    <UrgenciaBadge
                                        urgencia={detalleCuenta.venta?.urgencia}
                                        diasParaVencer={detalleCuenta.venta?.dias_para_vencer}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Productos */}
                        {detalleCuenta.productos?.length > 0 && (
                            <div className="detalle-section">
                                <h4>Productos</h4>
                                <table className="productos-table">
                                    <thead>
                                        <tr>
                                            <th>Codigo</th>
                                            <th>Producto</th>
                                            <th>Cant.</th>
                                            <th>Precio</th>
                                            <th>Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detalleCuenta.productos.map((prod, index) => (
                                            <tr key={index}>
                                                <td>{prod.codigo}</td>
                                                <td>{prod.nombre}</td>
                                                <td>{prod.cantidad}</td>
                                                <td>{formatMoney(prod.precio_unitario)}</td>
                                                <td>{formatMoney(prod.subtotal)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Totales */}
                        <div className="detalle-section totales">
                            <div className="total-row">
                                <span>Subtotal:</span>
                                <span>{formatMoney(detalleCuenta.venta?.subtotal)}</span>
                            </div>
                            {detalleCuenta.venta?.descuento > 0 && (
                                <div className="total-row descuento">
                                    <span>Descuento:</span>
                                    <span>-{formatMoney(detalleCuenta.venta?.descuento)}</span>
                                </div>
                            )}
                            <div className="total-row total">
                                <span>Total:</span>
                                <span>{formatMoney(detalleCuenta.venta?.total)}</span>
                            </div>
                            <div className="total-row abonado">
                                <span>Abonado:</span>
                                <span>{formatMoney(detalleCuenta.venta?.total_abonado)}</span>
                            </div>
                            <div className="total-row saldo">
                                <span>Saldo Pendiente:</span>
                                <span>{formatMoney(detalleCuenta.venta?.saldo_pendiente)}</span>
                            </div>
                        </div>

                        {/* Acciones */}
                        <div className="modal-actions">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowDetalleModal(false);
                                    setDetalleCuenta(null);
                                }}
                            >
                                Cerrar
                            </Button>
                            {canRegisterPaymentCXC() && detalleCuenta.venta?.saldo_pendiente > 0 && (
                                <Button
                                    onClick={() => {
                                        setShowDetalleModal(false);
                                        setCuentaActual({
                                            id: detalleCuenta.venta.id,
                                            folio: detalleCuenta.venta.folio,
                                            cliente_nombre: detalleCuenta.cliente?.nombre,
                                            total: detalleCuenta.venta.total,
                                            saldo_pendiente: detalleCuenta.venta.saldo_pendiente
                                        });
                                        setPagoForm({
                                            monto: '',
                                            metodo_pago: 'efectivo',
                                            referencia: '',
                                            notas: ''
                                        });
                                        setShowPagoModal(true);
                                    }}
                                >
                                    <DollarSign size={16} />
                                    Registrar Pago
                                </Button>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal: Abono Global Distribuido */}
            {showAbonoGlobalModal && clienteAbonoGlobal && (
                <Modal
                    title="Abono Global - Distribucion Automatica"
                    onClose={handleCloseAbonoGlobalModal}
                    size="large"
                >
                    <div className="abono-global-modal">
                        {/* Si ya se registro el abono, mostrar resultado */}
                        {resultadoAbonoGlobal ? (
                            <div className="resultado-abono">
                                <div className="resultado-header success">
                                    <CheckCircle size={48} />
                                    <h3>Abono Registrado Exitosamente</h3>
                                </div>

                                <div className="resultado-resumen">
                                    <div className="resumen-item">
                                        <span className="label">Monto aplicado:</span>
                                        <span className="value">{formatMoney(resultadoAbonoGlobal.monto_aplicado)}</span>
                                    </div>
                                    {resultadoAbonoGlobal.sobrante > 0 && (
                                        <div className="resumen-item sobrante">
                                            <span className="label">Sobrante (devolver):</span>
                                            <span className="value">{formatMoney(resultadoAbonoGlobal.sobrante)}</span>
                                        </div>
                                    )}
                                    <div className="resumen-item">
                                        <span className="label">Cuentas afectadas:</span>
                                        <span className="value">{resultadoAbonoGlobal.cuentas_afectadas}</span>
                                    </div>
                                    <div className="resumen-item">
                                        <span className="label">Cuentas liquidadas:</span>
                                        <span className="value">{resultadoAbonoGlobal.cuentas_liquidadas}</span>
                                    </div>
                                </div>

                                <div className="resultado-detalle">
                                    <h4>Detalle de la distribucion:</h4>
                                    <table className="distribucion-table">
                                        <thead>
                                            <tr>
                                                <th>Folio</th>
                                                <th>Saldo Anterior</th>
                                                <th>Aplicado</th>
                                                <th>Nuevo Saldo</th>
                                                <th>Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resultadoAbonoGlobal.detalle_pagos.map((pago, idx) => (
                                                <tr key={idx} className={pago.liquidada ? 'liquidada' : ''}>
                                                    <td>#{pago.folio}</td>
                                                    <td>{formatMoney(pago.saldo_anterior)}</td>
                                                    <td className="aplicado">-{formatMoney(pago.monto_aplicado)}</td>
                                                    <td>{formatMoney(pago.saldo_nuevo)}</td>
                                                    <td>
                                                        {pago.liquidada ? (
                                                            <span className="badge-liquidada">
                                                                <CheckCircle size={14} /> Liquidada
                                                            </span>
                                                        ) : (
                                                            <span className="badge-pendiente">Pendiente</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="modal-actions">
                                    <Button onClick={handleCloseAbonoGlobalModal}>
                                        Cerrar
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Info del cliente */}
                                <div className="cliente-info-card">
                                    <div className="cliente-header">
                                        <User size={32} />
                                        <div>
                                            <h4>{clienteAbonoGlobal.nombre}</h4>
                                            {clienteAbonoGlobal.nombre_comercial && (
                                                <span className="comercial">{clienteAbonoGlobal.nombre_comercial}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="cliente-stats">
                                        <div className="stat">
                                            <span className="label">Cuentas pendientes:</span>
                                            <span className="value">{cuentasClienteGlobal.length}</span>
                                        </div>
                                        <div className="stat total">
                                            <span className="label">Total adeudado:</span>
                                            <span className="value">
                                                {formatMoney(cuentasClienteGlobal.reduce((sum, c) => sum + c.saldo_pendiente, 0))}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Lista de cuentas a saldar */}
                                <div className="cuentas-a-saldar">
                                    <h4>Cuentas a saldar (orden de antiguedad):</h4>
                                    <div className="cuentas-list">
                                        {cuentasClienteGlobal.map((cuenta, idx) => (
                                            <div key={cuenta.id} className={`cuenta-item ${cuenta.urgencia}`}>
                                                <span className="orden">{idx + 1}</span>
                                                <div className="cuenta-info">
                                                    <span className="folio">#{cuenta.folio}</span>
                                                    <span className="fecha">{formatDate(cuenta.fecha_venta)}</span>
                                                </div>
                                                <span className="saldo">{formatMoney(cuenta.saldo_pendiente)}</span>
                                                <UrgenciaBadge urgencia={cuenta.urgencia} />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Formulario de abono */}
                                <div className="abono-form">
                                    <div className="form-field">
                                        <label>Monto del Abono *</label>
                                        <div className="monto-input-wrapper">
                                            <span className="currency">$</span>
                                            <input
                                                type="number"
                                                value={abonoGlobalForm.monto}
                                                onChange={(e) => handleAbonoGlobalFormChange('monto', e.target.value)}
                                                placeholder="0.00"
                                                min="0"
                                                step="0.01"
                                                className="monto-input"
                                            />
                                        </div>
                                        <div className="quick-amounts">
                                            <button
                                                type="button"
                                                onClick={() => handleAbonoGlobalFormChange('monto',
                                                    cuentasClienteGlobal.reduce((sum, c) => sum + c.saldo_pendiente, 0).toFixed(2)
                                                )}
                                                className="quick-btn"
                                            >
                                                Pagar Todo
                                            </button>
                                            {cuentasClienteGlobal.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleAbonoGlobalFormChange('monto',
                                                        cuentasClienteGlobal[0].saldo_pendiente.toFixed(2)
                                                    )}
                                                    className="quick-btn"
                                                >
                                                    1ra Cuenta ({formatMoney(cuentasClienteGlobal[0].saldo_pendiente)})
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-field">
                                            <label>Metodo de Pago</label>
                                            <select
                                                value={abonoGlobalForm.metodo_pago}
                                                onChange={(e) => handleAbonoGlobalFormChange('metodo_pago', e.target.value)}
                                                className="select-input"
                                            >
                                                <option value="efectivo">Efectivo</option>
                                                <option value="tarjeta">Tarjeta</option>
                                                <option value="transferencia">Transferencia</option>
                                            </select>
                                        </div>

                                        <div className="form-field">
                                            <label>Referencia (opcional)</label>
                                            <input
                                                type="text"
                                                value={abonoGlobalForm.referencia}
                                                onChange={(e) => handleAbonoGlobalFormChange('referencia', e.target.value)}
                                                placeholder="No. de transaccion"
                                                className="text-input"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        <label>Notas (opcional)</label>
                                        <textarea
                                            value={abonoGlobalForm.notas}
                                            onChange={(e) => handleAbonoGlobalFormChange('notas', e.target.value)}
                                            placeholder="Notas adicionales..."
                                            rows={2}
                                            className="textarea-input"
                                        />
                                    </div>
                                </div>

                                {/* Preview de la distribucion */}
                                {abonoGlobalForm.monto && parseFloat(abonoGlobalForm.monto) > 0 && (
                                    <div className="distribucion-preview">
                                        <h4>Vista previa de la distribucion:</h4>
                                        <table className="distribucion-table preview">
                                            <thead>
                                                <tr>
                                                    <th>Folio</th>
                                                    <th>Saldo Actual</th>
                                                    <th>Se Aplicara</th>
                                                    <th>Quedara</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {calcularDistribucionPrevia(parseFloat(abonoGlobalForm.monto)).map((dist, idx) => (
                                                    <tr key={idx} className={dist.liquidada ? 'will-liquidate' : ''}>
                                                        <td>#{dist.folio}</td>
                                                        <td>{formatMoney(dist.saldo_actual)}</td>
                                                        <td className="aplicar">-{formatMoney(dist.monto_aplicar)}</td>
                                                        <td>
                                                            {dist.liquidada ? (
                                                                <span className="liquidada-text">
                                                                    <CheckCircle size={14} /> Liquidada
                                                                </span>
                                                            ) : (
                                                                formatMoney(dist.nuevo_saldo)
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {parseFloat(abonoGlobalForm.monto) > cuentasClienteGlobal.reduce((sum, c) => sum + c.saldo_pendiente, 0) && (
                                            <div className="sobrante-warning">
                                                <AlertTriangle size={16} />
                                                Sobrante a devolver: {formatMoney(
                                                    parseFloat(abonoGlobalForm.monto) - cuentasClienteGlobal.reduce((sum, c) => sum + c.saldo_pendiente, 0)
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Acciones */}
                                <div className="modal-actions">
                                    <Button variant="secondary" onClick={handleCloseAbonoGlobalModal}>
                                        Cancelar
                                    </Button>
                                    <Button
                                        onClick={handleRegistrarAbonoGlobal}
                                        disabled={loading || !abonoGlobalForm.monto || parseFloat(abonoGlobalForm.monto) <= 0}
                                    >
                                        <Wallet size={16} />
                                        Registrar Abono Global
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {/* Modal: Cuentas del Cliente */}
            {showCuentasClienteModal && clienteCuentasModal && (
                <Modal
                    title={`Cuentas de ${clienteCuentasModal.nombre}`}
                    onClose={() => {
                        setShowCuentasClienteModal(false);
                        setClienteCuentasModal(null);
                        setCuentasDelCliente([]);
                    }}
                    size="large"
                >
                    <div className="cuentas-cliente-modal">
                        {/* Resumen del cliente */}
                        <div className="cliente-resumen-header">
                            <div className="cliente-info-resumen">
                                <User size={32} />
                                <div>
                                    <h4>{clienteCuentasModal.nombre}</h4>
                                    {clienteCuentasModal.nombre_comercial && (
                                        <span className="comercial">{clienteCuentasModal.nombre_comercial}</span>
                                    )}
                                </div>
                            </div>
                            <div className="cliente-totales">
                                <div className="total-item">
                                    <span className="label">Cuentas</span>
                                    <span className="value">{clienteCuentasModal.num_cuentas}</span>
                                </div>
                                <div className="total-item principal">
                                    <span className="label">Total Pendiente</span>
                                    <span className="value">{formatMoney(clienteCuentasModal.total_pendiente)}</span>
                                </div>
                                {clienteCuentasModal.cuentas_vencidas > 0 && (
                                    <div className="total-item vencidas">
                                        <span className="label">Vencidas</span>
                                        <span className="value">{clienteCuentasModal.cuentas_vencidas}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Tabla de cuentas */}
                        {cuentasDelCliente.length > 0 ? (
                            <div className="cuentas-tabla-container">
                                <table className="cuentas-cliente-table">
                                    <thead>
                                        <tr>
                                            <th>Folio</th>
                                            <th>Fecha</th>
                                            <th>Total</th>
                                            <th>Saldo</th>
                                            <th>Vencimiento</th>
                                            <th>Estado</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cuentasDelCliente.map(cuenta => (
                                            <tr key={cuenta.id} className={cuenta.urgencia === 'vencida' ? 'vencida' : ''}>
                                                <td className="folio">#{cuenta.folio}</td>
                                                <td>{formatDate(cuenta.fecha_venta)}</td>
                                                <td className="monto">{formatMoney(cuenta.total)}</td>
                                                <td className="monto saldo">{formatMoney(cuenta.saldo_pendiente)}</td>
                                                <td>{formatDate(cuenta.fecha_vencimiento)}</td>
                                                <td>
                                                    <UrgenciaBadge
                                                        urgencia={cuenta.urgencia}
                                                        diasParaVencer={cuenta.dias_para_vencer}
                                                    />
                                                </td>
                                                <td className="acciones">
                                                    <div className="acciones-btns">
                                                        {canRegisterPaymentCXC() && (
                                                            <button
                                                                className="action-btn pagar"
                                                                onClick={() => {
                                                                    setShowCuentasClienteModal(false);
                                                                    setCuentaActual({
                                                                        id: cuenta.id,
                                                                        folio: cuenta.folio,
                                                                        cliente_nombre: clienteCuentasModal.nombre,
                                                                        total: cuenta.total,
                                                                        saldo_pendiente: cuenta.saldo_pendiente
                                                                    });
                                                                    setPagoForm({
                                                                        monto: '',
                                                                        metodo_pago: 'efectivo',
                                                                        referencia: '',
                                                                        notas: ''
                                                                    });
                                                                    setShowPagoModal(true);
                                                                }}
                                                                title="Registrar pago"
                                                            >
                                                                <DollarSign size={14} />
                                                            </button>
                                                        )}
                                                        <button
                                                            className="action-btn historial"
                                                            onClick={() => {
                                                                setShowCuentasClienteModal(false);
                                                                handleVerHistorial(cuenta);
                                                            }}
                                                            title="Ver historial"
                                                        >
                                                            <History size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="empty-state small">
                                <CheckCircle size={32} />
                                <p>Este cliente no tiene cuentas pendientes</p>
                            </div>
                        )}

                        {/* Acciones */}
                        <div className="modal-actions">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowCuentasClienteModal(false);
                                    setClienteCuentasModal(null);
                                    setCuentasDelCliente([]);
                                }}
                            >
                                Cerrar
                            </Button>
                            {canRegisterPaymentCXC() && cuentasDelCliente.length > 0 && (
                                <Button
                                    onClick={() => {
                                        setShowCuentasClienteModal(false);
                                        handleOpenAbonoGlobalModal(clienteCuentasModal.id);
                                    }}
                                >
                                    <Wallet size={16} />
                                    Abono Global
                                </Button>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default CuentasPorCobrarPage;
